import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const STORAGE_KEY = "ccdb_pwa_dismissed_at";
const COOLDOWN_DAYS = 7;

export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) {
      setInstalled(true);
      return;
    }
    const dismissedAt = Number(localStorage.getItem(STORAGE_KEY) || 0);
    const isCoolingDown = dismissedAt && (Date.now() - dismissedAt) < COOLDOWN_DAYS * 24 * 3600 * 1000;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      if (isCoolingDown) return;
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setInstalled(true);
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  async function install() {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome !== "accepted") {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    }
    setVisible(false);
    setDeferred(null);
  }

  function dismiss() {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setVisible(false);
  }

  if (installed || !visible) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 left-4 right-4 z-40 mx-auto max-w-md border-2 border-slate-900 bg-white shadow-brutal animate-fade-up md:left-auto md:right-4"
    >
      <div className="flex items-stretch">
        <div className="flex items-center justify-center bg-safety-500 px-4">
          <Download className="h-5 w-5 text-slate-900" />
        </div>
        <div className="flex-1 p-3">
          <div className="industrial-label text-slate-900">Install NCC-SWMS</div>
          <p className="mt-1 font-mono text-xs text-slate-700">
            Add to home screen for instant access — works offline, camera uploads, app-like experience.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              onClick={install}
              data-testid="pwa-install-btn"
              className="border-2 border-slate-900 bg-slate-900 px-3 py-1.5 industrial-label text-white transition-all hover:-translate-y-0.5 hover:bg-safety-500 hover:text-slate-900 hover:shadow-brutal-sm"
            >
              Install
            </button>
            <button
              onClick={dismiss}
              data-testid="pwa-dismiss-btn"
              className="border-2 border-slate-300 bg-white px-3 py-1.5 industrial-label text-slate-700 transition-colors hover:border-slate-900"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Close"
          className="border-l-2 border-slate-200 px-3 hover:bg-slate-100"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
